/**
 * RegulatoryDocumentsPanel — first-class panel listing every official
 * document attached to a vaccine (PIL, SmPC, EPAR, FDA package insert, CDC
 * VIS, NHS leaflet, UKHSA Green Book chapter, WHO position paper, ...).
 *
 * Designed for parents and researchers spending 30–60 minutes per vaccine.
 * Grouped by regulator so the user can pick a jurisdiction quickly.
 * Each row links to the regulator's stable landing page (which then
 * resolves to the current versioned PDF) — we never link directly to a
 * versioned PDF URL because those rotate every SmPC revision.
 */
import React from "react";
import { ExternalLink, FileText, ShieldCheck, BookOpen, Pill, Globe2 } from "lucide-react";

const TYPE_META = {
    PIL:                 { Icon: Pill,        label: "Patient Information Leaflet" },
    SmPC:                { Icon: FileText,    label: "Summary of Product Characteristics" },
    EPAR:                { Icon: ShieldCheck, label: "European Public Assessment Report" },
    Package_Insert:      { Icon: FileText,    label: "FDA package insert" },
    VIS:                 { Icon: FileText,    label: "Vaccine Information Statement" },
    Patient_Leaflet:     { Icon: Pill,        label: "Patient leaflet" },
    Green_Book:          { Icon: BookOpen,    label: "Green Book chapter" },
    Schedule_Document:   { Icon: BookOpen,    label: "Schedule document" },
    WHO_Position_Paper:  { Icon: Globe2,      label: "WHO position paper" },
    FDA_Approval:        { Icon: ShieldCheck, label: "FDA approval" },
    Regulator_Page:      { Icon: ShieldCheck, label: "Regulator information" },
    Other:               { Icon: FileText,    label: "Document" },
};

const REGULATOR_ORDER = ["NHS", "MHRA", "UKHSA", "EMA", "FDA", "CDC", "WHO", ""];

export default function RegulatoryDocumentsPanel({ documents = [], className = "" }) {
    if (!documents.length) return null;

    // Group by regulator while preserving an intuitive ordering.
    const grouped = {};
    for (const d of documents) {
        const r = d.regulator || "Other";
        (grouped[r] = grouped[r] || []).push(d);
    }
    const sortedRegulators = Object.keys(grouped).sort((a, b) => {
        const ai = REGULATOR_ORDER.indexOf(a);
        const bi = REGULATOR_ORDER.indexOf(b);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi) || a.localeCompare(b);
    });

    return (
        <section
            data-testid="regulatory-documents-panel"
            className={`bg-[#111827] border border-[#243044] rounded-xl p-6 ${className}`}
        >
            <header className="mb-5">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] font-medium mb-1.5">Official documents</p>
                <h2 className="text-xl font-semibold text-white">Regulatory documents &amp; references</h2>
                <p className="text-xs text-[#64748B] mt-1.5">
                    Direct links to the issuing regulator's landing page. Each page resolves to the latest versioned PIL / SmPC / package insert.
                </p>
            </header>

            <div className="space-y-5">
                {sortedRegulators.map((reg) => (
                    <div key={reg} data-testid={`regdocs-group-${reg}`}>
                        <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#94A3B8] font-medium mb-2">{reg}</h3>
                        <ul className="space-y-1.5">
                            {grouped[reg].map((d, i) => {
                                const meta = TYPE_META[d.document_type] || TYPE_META.Other;
                                const Icon = meta.Icon;
                                return (
                                    <li key={`${d.url}-${i}`}>
                                        <a
                                            href={d.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`regdoc-link-${d.document_type}`}
                                            className="group flex items-start gap-3 px-3 py-2 -mx-3 rounded-md hover:bg-[#0B1220] transition-colors"
                                        >
                                            <Icon className="w-3.5 h-3.5 text-[#56CCF2] mt-1 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-white group-hover:text-[#56CCF2] transition-colors leading-snug">
                                                    {d.label}
                                                </div>
                                                <div className="text-[10px] uppercase tracking-[0.15em] text-[#64748B] mt-0.5">
                                                    {meta.label}
                                                    {d.country ? ` · ${d.country}` : ""}
                                                </div>
                                            </div>
                                            <ExternalLink className="w-3.5 h-3.5 text-[#64748B] shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
            </div>
        </section>
    );
}
